import { AccountSecurityPage } from '@/account/pages/account-security/account-security-page';
import InvitationSignupPage from '@/auth/pages/invited/invited';
import { ResetPasswordPage } from '@/auth/pages/reset-password-page/reset-password-page';
import {
  ForbiddenPage,
  NotFoundPage,
  ProtectedRoute,
  UserProvider,
  VenueContext,
  useSelfAppDataSettled,
  useUser,
  zApplications,
} from '@idu-nightlife/ui-kit';
import React, { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { AppLayout } from '../../../app-layout';
import { ForgotPasswordPage } from '../../../auth/pages/forgot-password-page/forgot-password-page';
import { LoginPage } from '../../../auth/pages/login-page/login-page';
import { DefaultAppPage } from '../../../main/pages/default-app-page';
import { LandingPage } from '../../../main/pages/landing-page';
import type { AppConfig } from '../../../shared/components/global-nav/app-config';
import {
  configs,
  nightlifeConfigs,
  nightlifeDashboardConfig,
} from '../../../shared/components/global-nav/app-config';
import { ManagerSelectionProvider } from '../../contexts/manager-selection';

function getConfig(id: AppConfig['id']): AppConfig {
  const config = configs.find((c) => c.id === id);
  if (!config) {
    throw new Error(`Missing app config for ${String(id)}`);
  }
  return config;
}

function ManagerVenueProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const isAuthorityResolved = useSelfAppDataSettled();

  const values = useMemo(() => {
    const allVenues = user?.venues || [];
    return {
      allVenues,
      activeVenue: allVenues[0] || null,
      applicationVenues: allVenues,
      changeVenue: async () => {},
      isAuthorityResolved,
    };
  }, [user, isAuthorityResolved]);

  return <VenueContext.Provider value={values}>{children}</VenueContext.Provider>;
}

function AuthenticatedRoutes() {
  const checkdConfig = getConfig(zApplications.enum.checkd);
  const dashboardConfig = getConfig(zApplications.enum.idu_dashboard);

  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route path="/forbidden" element={<ForbiddenPage />} />
        <Route path="/account/security" element={<AccountSecurityPage />} />

        {/* Legacy kebab-case prefixes */}
        <Route
          path="/pokie-payouts/*"
          element={<Navigate to={`/${zApplications.enum.checkd}`} replace />}
        />
        <Route
          path="/idu-dashboard/*"
          element={<Navigate to={`/${zApplications.enum.idu_dashboard}`} replace />}
        />

        {/* Default entry — ManagerSelectionProvider materialises the
            default application/venue via the shared resolution rule (FR-010) */}
        <Route path="/" element={<DefaultAppPage />} />

        {/* Per-app landing pages */}
        <Route
          path={`/${zApplications.enum.checkd}/*`}
          element={<LandingPage key={checkdConfig.id} app={checkdConfig} />}
        />

        <Route
          path={`/${zApplications.enum.idu_dashboard}/*`}
          element={<LandingPage key={dashboardConfig.id} app={dashboardConfig} />}
        />

        {[nightlifeDashboardConfig, ...nightlifeConfigs].map((config) => (
          <Route
            key={config.id}
            path={`/${config.id}/*`}
            element={<LandingPage key={config.id} app={config} />}
          />
        ))}
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/invited/:inviteId" element={<InvitationSignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      <Route
        path="/*"
        element={
          <ProtectedRoute unauthenticatedPath="/login">
            <UserProvider>
              <ManagerVenueProvider>
                <ManagerSelectionProvider>
                  <AuthenticatedRoutes />
                </ManagerSelectionProvider>
              </ManagerVenueProvider>
            </UserProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}
